import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AdminLicense, adminListLicenses, readAxiosDetail } from './api_calls';

function getLicenseStatus(
  lic: AdminLicense,
  t: (key: string) => string,
): { label: string; color: 'success' | 'warning' | 'error' | 'default' } {
  if (!lic.user_id) return { label: t('licenses.status.unassigned'), color: 'default' };
  const now = Date.now();
  const exp = new Date(lic.expires_at).getTime();
  if (exp < now) return { label: t('licenses.status.expired'), color: 'error' };
  const daysLeft = (exp - now) / (1000 * 60 * 60 * 24);
  if (daysLeft < 30) return { label: t('licenses.status.expiringSoon'), color: 'warning' };
  return { label: t('licenses.status.active'), color: 'success' };
}

export default function LicensesTab(): React.ReactElement {
  const { t } = useTranslation('admin');
  const [licenses, setLicenses] = useState<AdminLicense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminListLicenses();
      setLicenses(data);
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('licenses.columns.user')}</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{t('licenses.columns.customer')}</TableCell>
              <TableCell>{t('licenses.columns.expires')}</TableCell>
              <TableCell>{t('licenses.columns.status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {licenses.map((lic) => {
              const status = getLicenseStatus(lic, t);
              return (
                <TableRow key={lic.license_id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {lic.user_email || '—'}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: { xs: 'block', md: 'none' } }}
                    >
                      {lic.customer_name}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{lic.customer_name}</TableCell>
                  <TableCell>{new Date(lic.expires_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Chip label={status.label} size="small" color={status.color} variant="outlined" />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {licenses.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          {t('licenses.empty')}
        </Typography>
      )}
    </Box>
  );
}
